import React, { useContext, useEffect, useMemo, useState } from "react";
import { useLocation } from "react-router-dom";
import { transferNFT } from "../api/nft";
import { fetchUserByUUID } from "../api/users";
import { AuthContext } from "../contexts/AuthContext";
import { listCompanies } from "../api/companies";
import { validateCertificationNumber, validateEmail, validateNumistrailId } from "../utils/validation";
import { useTranslation } from "react-i18next";
import { resolveApiMessage } from "../utils/apiMessages";

const RECIPIENT_MODE_NUMISTRAIL = "numistrail";
const RECIPIENT_MODE_EMAIL = "email";

export function Reassign() {
  const location = useLocation();
  const defaults = location.state?.transferDefaults;
  const [form, setForm] = useState({
    recipientMode: RECIPIENT_MODE_NUMISTRAIL,
    recipientId: "",
    recipientEmail: "",
    recipientEmailConfirm: "",
    cert: "",
    auth: "",
  });
  const [status, setStatus] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [touched, setTouched] = useState({});
  const [currentUsername, setCurrentUsername] = useState(null);
  const [authorities, setAuthorities] = useState([]);
  const [isConsignment, setIsConsignment] = useState(false);
  const { userId } = useContext(AuthContext);
  const { t } = useTranslation();

  useEffect(() => {
    if (!defaults) {
      return;
    }
    setForm((current) => ({
      ...current,
      cert: defaults.cert || "",
      auth: defaults.auth || "",
    }));
  }, [defaults]);

  useEffect(() => {
    if (!defaults || !defaults.auth || authorities.length === 0) {
      return;
    }
    const bySlug = authorities.find((authority) => authority.slug === defaults.auth);
    if (bySlug) {
      setForm((current) => ({ ...current, auth: bySlug.slug }));
      return;
    }
    const byDisplay = authorities.find((authority) => authority.display_name === defaults.auth);
    if (byDisplay) {
      setForm((current) => ({ ...current, auth: byDisplay.slug }));
    }
  }, [authorities, defaults]);

  useEffect(() => {
    if (!userId) {
      return;
    }
    let isMounted = true;
    fetchUserByUUID(userId)
      .then(({ data }) => {
        if (!isMounted) return;
        setCurrentUsername(data.numistrail_id || null);
      })
      .catch(() => {
        if (!isMounted) return;
        setCurrentUsername(null);
      });
    return () => {
      isMounted = false;
    };
  }, [userId]);

  useEffect(() => {
    listCompanies({ companyType: "CERTIFICATION_ENTITY" })
      .then((resp) => setAuthorities(resp.data.companies || []))
      .catch(() => setAuthorities([]));
  }, []);

  useEffect(() => {
    if (!isConsignment) {
      return;
    }
    setForm((current) => {
      if (current.recipientMode === RECIPIENT_MODE_NUMISTRAIL) {
        return current;
      }
      return {
        ...current,
        recipientMode: RECIPIENT_MODE_NUMISTRAIL,
        recipientEmail: "",
        recipientEmailConfirm: "",
      };
    });
    setTouched((prev) => {
      if (!prev.recipientEmail && !prev.recipientEmailConfirm && !prev.recipientMode) {
        return prev;
      }
      return {
        ...prev,
        recipientMode: true,
        recipientEmail: false,
        recipientEmailConfirm: false,
      };
    });
  }, [isConsignment]);

  const handleChange = (key) => (e) => {
    const value = e.target.value;
    setForm((current) => {
      const next = { ...current, [key]: value };
      if (key === "recipientMode") {
        next.recipientId = "";
        next.recipientEmail = "";
        next.recipientEmailConfirm = "";
      }
      return next;
    });
    setTouched((prev) => (prev[key] ? prev : { ...prev, [key]: true }));
  };

  const markTouched = (key) => () =>
    setTouched((prev) => ({ ...prev, [key]: true }));

  const blockCopyPaste = (event) => {
    event.preventDefault();
  };

  const validationErrors = useMemo(() => {
    const isEmailMode = form.recipientMode === RECIPIENT_MODE_EMAIL && !isConsignment;
    const normalizedEmail = form.recipientEmail.trim().toLowerCase();
    const normalizedEmailConfirm = form.recipientEmailConfirm.trim().toLowerCase();
    const recipientEmailError = isEmailMode ? validateEmail(form.recipientEmail) : null;
    let recipientEmailConfirmError = null;
    if (isEmailMode) {
      recipientEmailConfirmError = validateEmail(form.recipientEmailConfirm);
      if (!recipientEmailError && !recipientEmailConfirmError && normalizedEmail !== normalizedEmailConfirm) {
        recipientEmailConfirmError = "transfer.errors.emailMismatch";
      }
    }

    return {
      recipientId: !isEmailMode ? validateRecipientId(form.recipientId, currentUsername) : null,
      recipientEmail: recipientEmailError,
      recipientEmailConfirm: recipientEmailConfirmError,
      auth: validateAuthority(form.auth, authorities),
      cert: validateCertificationNumber(form.cert),
    };
  }, [
    form.recipientMode,
    form.recipientId,
    form.recipientEmail,
    form.recipientEmailConfirm,
    form.auth,
    form.cert,
    currentUsername,
    authorities,
    isConsignment,
  ]);

  const isValid = useMemo(
    () => Object.values(validationErrors).every((error) => !error),
    [validationErrors]
  );

  const handleSubmit = async (event) => {
    event.preventDefault();

    const isEmailMode = form.recipientMode === RECIPIENT_MODE_EMAIL && !isConsignment;
    const nextTouched = {
      recipientMode: true,
      cert: true,
      auth: true,
      recipientId: !isEmailMode,
      recipientEmail: isEmailMode,
      recipientEmailConfirm: isEmailMode,
    };

    if (!isValid || isSubmitting) {
      setTouched((prev) => ({ ...prev, ...nextTouched }));
      return;
    }

    const normalizedRecipientId = form.recipientId.trim().toLowerCase();
    const normalizedRecipientEmail = form.recipientEmail.trim().toLowerCase();
    const normalizedCert = form.cert.trim();
    const selectedAuthority = authorities.find((authority) => authority.slug === form.auth);
    const authorityLabel = selectedAuthority?.display_name || form.auth;
    const recipientLabel = isEmailMode ? normalizedRecipientEmail : normalizedRecipientId;

    const actionVerb = isConsignment ? t("transfer.confirm.consignVerb") : t("transfer.confirm.transferVerb");
    if (!window.confirm(t("transfer.confirm.text", { action: actionVerb, cert: normalizedCert, authority: authorityLabel, recipient: recipientLabel }))) {
      return;
    }

    setIsSubmitting(true);
    setStatus(null);

    try {
      const { data } = await transferNFT(
        normalizedCert,
        authorityLabel,
        isEmailMode ? null : normalizedRecipientId,
        {
          consignment: isConsignment,
          recipientEmail: isEmailMode ? normalizedRecipientEmail : null,
        }
      );
      setStatus({
        type: "success",
        text: t(isConsignment ? "transfer.success.consignment" : "transfer.success.transfer", { tx: data.blockchain_tx }),
      });
      setForm({
        recipientMode: RECIPIENT_MODE_NUMISTRAIL,
        recipientId: "",
        recipientEmail: "",
        recipientEmailConfirm: "",
        cert: "",
        auth: "",
      });
      setTouched({});
      setIsConsignment(false);
    } catch (e) {
      const apiMessage = resolveApiMessage(e, t, null, null);
      const userMessage = t(normalizeTransferError(e.response?.data), { defaultValue: apiMessage || t("errors.generic") });
      setStatus({ type: "error", text: userMessage });
    } finally {
      setIsSubmitting(false);
    }
  };

  const isEmailMode = form.recipientMode === RECIPIENT_MODE_EMAIL && !isConsignment;

  return (
    <div className="transfer-screen">
      <div className="transfer-card">
        <div className="transfer-card__header">
          <h2 className="transfer-title">{t("transfer.title")}</h2>
          <p className="transfer-subtitle">
            {t("transfer.subtitle")}
          </p>
        </div>
        <form className="transfer-form" onSubmit={handleSubmit}>
          <div className="transfer-field">
            <label htmlFor="recipient-mode" className="transfer-label">
              {t("transfer.fields.recipientMode.label")}
            </label>
            <select
              id="recipient-mode"
              value={isConsignment ? RECIPIENT_MODE_NUMISTRAIL : form.recipientMode}
              onChange={handleChange("recipientMode")}
              onBlur={markTouched("recipientMode")}
              disabled={isConsignment}
            >
              <option value={RECIPIENT_MODE_NUMISTRAIL}>
                {t("transfer.fields.recipientMode.numistrail")}
              </option>
              <option value={RECIPIENT_MODE_EMAIL}>
                {t("transfer.fields.recipientMode.email")}
              </option>
            </select>
          </div>

          {!isEmailMode && (
            <div className="transfer-field">
              <label htmlFor="recipient" className="transfer-label">
                {t("transfer.fields.recipient.label")}
              </label>
              <input
                id="recipient"
                type="text"
                placeholder={t("transfer.fields.recipient.placeholder")}
                value={form.recipientId}
                onChange={handleChange("recipientId")}
                onBlur={markTouched("recipientId")}
              />
              {touched.recipientId && validationErrors.recipientId ? (
                <p className="transfer-field__error" role="alert">{t(validationErrors.recipientId)}</p>
              ) : null}
            </div>
          )}

          {isEmailMode && (
            <>
              <div className="transfer-field">
                <label htmlFor="recipient-email" className="transfer-label">
                  {t("transfer.fields.recipientEmail.label")}
                </label>
                <input
                  id="recipient-email"
                  type="email"
                  inputMode="email"
                  autoComplete="off"
                  placeholder={t("transfer.fields.recipientEmail.placeholder")}
                  value={form.recipientEmail}
                  onChange={handleChange("recipientEmail")}
                  onBlur={markTouched("recipientEmail")}
                />
                {touched.recipientEmail && validationErrors.recipientEmail ? (
                  <p className="transfer-field__error" role="alert">{t(validationErrors.recipientEmail)}</p>
                ) : null}
              </div>

              <div className="transfer-field">
                <label htmlFor="recipient-email-confirm" className="transfer-label">
                  {t("transfer.fields.recipientEmailConfirm.label")}
                </label>
                <input
                  id="recipient-email-confirm"
                  type="email"
                  inputMode="email"
                  autoComplete="off"
                  placeholder={t("transfer.fields.recipientEmailConfirm.placeholder")}
                  value={form.recipientEmailConfirm}
                  onChange={handleChange("recipientEmailConfirm")}
                  onBlur={markTouched("recipientEmailConfirm")}
                  onCopy={blockCopyPaste}
                  onPaste={blockCopyPaste}
                  onCut={blockCopyPaste}
                  onDrop={blockCopyPaste}
                />
                {touched.recipientEmailConfirm && validationErrors.recipientEmailConfirm ? (
                  <p className="transfer-field__error" role="alert">{t(validationErrors.recipientEmailConfirm)}</p>
                ) : null}
              </div>
            </>
          )}

          <div className="transfer-field">
            <label htmlFor="authority" className="transfer-label">
              {t("transfer.fields.authority.label")}
            </label>
            <select
              id="authority"
              value={form.auth}
              onChange={handleChange("auth")}
              onBlur={markTouched("auth")}
            >
              <option value="" disabled>
                {t("transfer.fields.authority.placeholder")}
              </option>
              {authorities.map((authority) => (
                <option key={authority.slug} value={authority.slug}>
                  {authority.display_name}
                </option>
              ))}
            </select>
            {touched.auth && validationErrors.auth ? (
              <p className="transfer-field__error" role="alert">{t(validationErrors.auth)}</p>
            ) : null}
          </div>

          <div className="transfer-field">
            <label htmlFor="cert-number" className="transfer-label">
              {t("transfer.fields.cert.label")}
            </label>
            <input
              id="cert-number"
              type="text"
              placeholder={t("transfer.fields.cert.placeholder")}
              value={form.cert}
              onChange={handleChange("cert")}
              onBlur={markTouched("cert")}
            />
            {touched.cert && validationErrors.cert ? (
              <p className="transfer-field__error" role="alert">{t(validationErrors.cert)}</p>
            ) : null}
          </div>

          <div className="transfer-field transfer-field--checkbox">
            <label className="transfer-checkbox">
              <input
                type="checkbox"
                checked={isConsignment}
                onChange={(event) => setIsConsignment(event.target.checked)}
              />
              <span>{t("transfer.fields.consignment.label")}</span>
            </label>
          </div>

          {status && (
            <p
              className={`transfer-status ${
                status.type === "error" ? "transfer-status--error" : ""
              }`}
            >
              {status.text}
            </p>
          )}

          <button
            type="submit"
            className="transfer-submit"
            disabled={!isValid || isSubmitting}
          >
            {isSubmitting
              ? (isConsignment ? t("transfer.buttons.consigning") : t("transfer.buttons.transferring"))
              : (isConsignment ? t("transfer.buttons.consign") : t("transfer.buttons.transfer"))}
          </button>
        </form>
      </div>
    </div>
  );
}

function normalizeTransferError(errorData) {
  const messageKey = errorData?.message_key || "";
  const normalized = (errorData?.message || "").toLowerCase();
  if (messageKey === "errors.owner.not_found") {
    return "transfer.errors.recipientNotFound";
  }
  if (messageKey === "errors.nft.not_found") {
    return "transfer.errors.notFound";
  }
  if (normalized.includes("already consigned")) {
    return "transfer.errors.alreadyConsigned";
  }
  if (normalized.includes("only the current owner") || normalized.includes("consigned nfts")) {
    return "transfer.errors.notOwner";
  }
  if (normalized.includes("not found")) {
    return "transfer.errors.notFound";
  }
  if (normalized.includes("consignment")) {
    return "transfer.errors.consignmentRole";
  }
  return "transfer.errors.generic";
}

function validateRecipientId(value, currentUsername) {
  const trimmed = value.trim();
  const baseError = validateNumistrailId(trimmed);
  if (baseError) {
    return baseError;
  }
  if (currentUsername && trimmed.toLowerCase() === currentUsername.toLowerCase()) {
    return "transfer.errors.self";
  }
  return null;
}

function validateAuthority(value, authorities) {
  if (!value) {
    return "transfer.errors.authorityRequired";
  }
  if (authorities && authorities.length > 0) {
    const slugSet = new Set(authorities.map((authority) => authority.slug));
    if (!slugSet.has(value)) {
      return "transfer.errors.authorityInvalid";
    }
  }
  return null;
}
